import { useEffect, useState, type DragEvent } from 'react';
import { Dialog, Button, Input } from '@/design-system';
import { parseImportText } from '@/domain/export-import/parse-import-package';
import {
  ImportPackageError,
  MAX_IMPORT_BYTES,
  type DarkFunnelsExportPackage,
  type ImportPreview,
} from '@/domain/export-import/contracts';
import * as dashboardApi from '@/features/dashboard/api/dashboard-api';
import { confirmImport } from './api/import-api';
import { navigate } from '@/app/router';
import type { Folder } from '@/contracts/funnel';

type Prepared = { package: DarkFunnelsExportPackage; preview: ImportPreview };

const WARNING_TEXT: Record<string, string> = {
  attachments_not_included: 'Os anexos privados não estão incluídos.',
  logo_not_included: 'O logotipo privado da apresentação não está incluído.',
  unknown_node_type: 'Tipos de nó desconhecidos serão preservados com renderização alternativa.',
  unknown_edge_type: 'Tipos de conexão desconhecidos serão preservados com renderização alternativa.',
  schema_migrated: 'O grafo foi migrado para o esquema atual.',
};

function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Não foi possível ler o arquivo selecionado.'));
    reader.readAsText(file, 'utf-8');
  });
}

export function ImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [prepared, setPrepared] = useState<Prepared | null>(null);
  const [name, setName] = useState('');
  const [folderId, setFolderId] = useState('');
  const [folders, setFolders] = useState<Folder[]>([]);
  const [status, setStatus] = useState<'idle' | 'validating' | 'ready' | 'creating' | 'error'>(
    'idle',
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) void dashboardApi.listFolders().then((response) => setFolders(response.items));
  }, [open]);

  async function selectFile(file: File) {
    setError(null);
    setPrepared(null);
    setStatus('validating');
    try {
      if (file.size > MAX_IMPORT_BYTES) {
        throw new ImportPackageError(
          'IMPORT_FILE_TOO_LARGE',
          'O arquivo JSON deve ter até 5 MB.',
        );
      }
      const parsed = parseImportText(await readFileText(file), file.size);
      setPrepared({ package: parsed.package, preview: parsed.preview });
      setName(parsed.preview.name);
      setStatus('ready');
    } catch (reason) {
      setStatus('error');
      setError(reason instanceof Error ? reason.message : 'Não foi possível ler o arquivo selecionado.');
    }
  }

  function drop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) void selectFile(file);
  }

  async function create() {
    if (!prepared) return;
    setStatus('creating');
    setError(null);
    try {
      const result = await confirmImport({
        package: prepared.package,
        name,
        folderId: folderId || null,
      });
      navigate(`/app/funnels/${result.funnel.id}`);
    } catch (reason) {
      setStatus('error');
      setError(reason instanceof Error ? reason.message : 'Não foi possível importar o funil.');
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Importar funil">
      <div className="space-y-4">
        <label
          onDragOver={(event) => event.preventDefault()}
          onDrop={drop}
          className="block cursor-pointer rounded border border-dashed border-border-brand p-6 text-center text-sm text-text-secondary"
        >
          Arraste um arquivo JSON do Dark Funnels aqui ou escolha um arquivo
          <input
            aria-label="Escolher arquivo para importar"
            type="file"
            accept=".json,application/json"
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void selectFile(file);
            }}
          />
        </label>
        <div role="status" aria-live="polite" className="text-xs text-text-muted">
          {status === 'validating' ? 'Validando…' : status === 'creating' ? 'Criando…' : ''}
        </div>
        {error ? (
          <p role="alert" className="text-sm text-[#f08484]">
            {error}
          </p>
        ) : null}
        {prepared ? (
          <section
            role="region"
            aria-label="Prévia da importação"
            className="space-y-3 rounded border border-border-subtle p-3"
          >
            <h3 className="font-medium">Prévia</h3>
            <p className="text-xs text-text-secondary">
              {prepared.preview.nodeCount} nós · {prepared.preview.edgeCount} conexões ·{' '}
              {prepared.preview.scenarioCount} cenários · esquema {prepared.preview.schemaVersion}
            </p>
            {prepared.preview.warnings.length ? (
              <ul aria-label="Avisos da importação" className="list-disc pl-5 text-xs text-[#e0b866]">
                {prepared.preview.warnings.map((warning) => (
                  <li key={warning}>{WARNING_TEXT[warning]}</li>
                ))}
              </ul>
            ) : null}
            <label className="block text-sm">
              Nome do funil
              <Input
                value={name}
                maxLength={120}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label className="block text-sm">
              Pasta (opcional)
              <select
                aria-label="Pasta de importação"
                className="mt-1 w-full rounded bg-bg-app p-2"
                value={folderId}
                onChange={(event) => setFolderId(event.target.value)}
              >
                <option value="">Sem pasta</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </label>
          </section>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={!prepared || !name.trim() || status === 'creating'}
            onClick={() => void create()}
          >
            Criar novo funil
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
